import type {
  AlgorhythmMetadataRequest,
  LookAssetUpdateRequest,
  LooksMetadataRequest,
  MoveAssetUpdateRequest,
  MovesMetadataRequest,
  SongAssetUpdateRequest,
  SongMetadataRequest,
  StarAssetUpdateRequest,
  StarMetadataRequest,
  WorldAssetUpdateRequest,
  WorldsMetadataRequest,
} from '../contracts/openapi/assetContracts';

type SupportedUpdateLayer = 'G' | 'S' | 'L' | 'M' | 'W';

export type CanonicalLayerAssetUpdateRequest =
  | SongAssetUpdateRequest
  | StarAssetUpdateRequest
  | LookAssetUpdateRequest
  | MoveAssetUpdateRequest
  | WorldAssetUpdateRequest;

export interface BuildLayerAssetUpdateRequestParams {
  layer: SupportedUpdateLayer;
  description: string;
  tags: string[];
  songMetadata?: unknown;
  starMetadata?: unknown;
  looksMetadata?: unknown;
  movesMetadata?: unknown;
  worldsMetadata?: unknown;
}

const SONG_METADATA_KEYS = [
  'energy',
  'culturalOrigin',
  'style',
  'genre',
  'duration',
  'bpm',
  'key',
  'mood',
  'songName',
  'artistName',
  'albumName',
  'tempo',
  'timeSignature',
  'releaseYear',
  'albumArtUrl',
] as const;

const STAR_METADATA_KEYS = [
  'energy',
  'culturalOrigin',
  'starName',
  'realName',
  'gender',
  'ageGroup',
  'ethnicity',
  'bodyType',
  'height',
  'hairColor',
  'hairLength',
  'hairStyle',
  'eyeColor',
  'skinTone',
  'archetype',
  'mood',
  'musicalStyle',
  'careerStage',
  'personalityTraits',
  'performanceContext',
  'makeupType',
  'colorPalette',
  'hairTexture',
  'hairTreatment',
  'hairAccessories',
  'eyeMakeup',
  'lipMakeup',
  'faceMakeup',
  'makeupEffects',
] as const;

const LOOKS_METADATA_KEYS = [
  'energy',
  'culturalOrigin',
  'outfitName',
  'styleCategory',
  'style',
  'gender',
  'occasion',
  'colorScheme',
  'primaryGarment',
  'mood',
  'primaryColors',
  'patterns',
  'targetAudience',
  'performanceContext',
  'culturalContext',
  'materials',
  'seasonality',
  'colorPalette',
  'timeOfDay',
  'accessories',
  'jewelryType',
  'brandNames',
  'priceRange',
  'formality',
] as const;

const MOVES_METADATA_KEYS = [
  'energy',
  'culturalOrigin',
  'tempo',
  'moveName',
  'danceStyle',
  'difficulty',
  'mood',
  'bodyParts',
  'choreographerName',
  'substyle',
  'complexity',
  'intensity',
  'pace',
  'movementStyle',
  'spaceRequired',
  'groupSize',
  'skillLevelRequired',
  'costumeRequirements',
  'targetAudience',
  'performanceContext',
  'audienceType',
  'bpmRangeMin',
  'bpmRangeMax',
  'floorWork',
  'partnered',
  'signatureMoves',
  'techniquesRequired',
  'propsNeeded',
] as const;

const WORLDS_METADATA_KEYS = [
  'energy',
  'culturalOrigin',
  'style',
  'mood',
  'timeOfDay',
  'season',
  'versatility',
  'targetAudience',
  'performanceContext',
  'culturalContext',
  'worldName',
  'locationType',
  'lighting',
  'dominantColors',
  'atmosphere',
  'weather',
  'setting',
  'environmentType',
  'lightingStyle',
  'spatialSize',
  'settingCategory',
  'specificLocation',
  'spaceSize',
  'layout',
  'depth',
  'colorPalette',
  'lightingDirection',
  'geographicRegion',
  'architecturalStyle',
  'backdropType',
  'productionValue',
  'technicalComplexity',
  'movementSpace',
  'audioAcoustics',
  'crowdCapacity',
] as const;

const ALGORHYTHM_KEYS = [
  'targetAudience',
  'ageAppropriateness',
  'culturalContext',
  'musicalStyle',
  'performanceContext',
] as const;

type UnknownRecord = Record<string, unknown>;

const isNonEmptyString = (value: unknown): value is string =>
  typeof value === 'string' && value.trim().length > 0;

const asRecord = (value: unknown): UnknownRecord =>
  value && typeof value === 'object' && !Array.isArray(value)
    ? (value as UnknownRecord)
    : {};

const sanitizeStringArray = (value: unknown): string[] | undefined => {
  if (Array.isArray(value)) {
    const result = value
      .map(item => (typeof item === 'string' ? item.trim() : String(item).trim()))
      .filter(Boolean);
    return result.length > 0 ? result : undefined;
  }

  if (isNonEmptyString(value)) {
    return [value.trim()];
  }

  return undefined;
};

const assignIfDefined = (
  target: UnknownRecord,
  key: string,
  value: unknown
) => {
  if (value === undefined || value === null) {
    return;
  }

  if (typeof value === 'string') {
    if (value.trim().length === 0) {
      return;
    }
    target[key] = value.trim();
    return;
  }

  if (Array.isArray(value)) {
    if (value.length === 0) {
      return;
    }
    target[key] = value;
    return;
  }

  target[key] = value;
};

const sanitizeTags = (tags: string[]) =>
  tags.map(tag => tag.trim()).filter(Boolean);

const buildAlgorhythmMetadata = (
  source: UnknownRecord
): AlgorhythmMetadataRequest | undefined => {
  const direct = asRecord(source.algorhythmMetadata);
  const metadata: Partial<AlgorhythmMetadataRequest> = {};

  ALGORHYTHM_KEYS.forEach(key => {
    const value = sanitizeStringArray(direct[key] ?? source[key]);
    if (value) {
      metadata[key] = value as never;
    }
  });

  return Object.keys(metadata).length > 0
    ? (metadata as AlgorhythmMetadataRequest)
    : undefined;
};

const buildSongMetadataRequest = (value: unknown): SongMetadataRequest => {
  const source = asRecord(value);
  const metadata: Partial<SongMetadataRequest> = {};

  SONG_METADATA_KEYS.forEach(key => {
    const rawValue =
      key === 'genre' || key === 'mood'
        ? sanitizeStringArray(source[key])
        : source[key];
    assignIfDefined(metadata as UnknownRecord, key, rawValue);
  });

  const algorhythmMetadata = buildAlgorhythmMetadata(source);
  if (algorhythmMetadata) {
    metadata.algorhythmMetadata = algorhythmMetadata;
  }

  return metadata as SongMetadataRequest;
};

const buildStarMetadataRequest = (value: unknown): StarMetadataRequest => {
  const source = asRecord(value);
  const flatSource = {
    ...source,
    ...asRecord(source.physicalTraits),
  };
  const metadata: Partial<StarMetadataRequest> = {};

  STAR_METADATA_KEYS.forEach(key => {
    const rawValue =
      key === 'mood' ||
      key === 'musicalStyle' ||
      key === 'personalityTraits' ||
      key === 'performanceContext'
        ? sanitizeStringArray(flatSource[key])
        : flatSource[key];
    assignIfDefined(metadata as UnknownRecord, key, rawValue);
  });

  return metadata as StarMetadataRequest;
};

const buildLooksMetadataRequest = (value: unknown): LooksMetadataRequest => {
  const source = asRecord(value);
  const metadata: Partial<LooksMetadataRequest> = {};

  LOOKS_METADATA_KEYS.forEach(key => {
    const rawValue =
      key === 'mood' ||
      key === 'primaryColors' ||
      key === 'patterns' ||
      key === 'targetAudience' ||
      key === 'performanceContext' ||
      key === 'culturalContext' ||
      key === 'materials' ||
      key === 'accessories' ||
      key === 'brandNames'
        ? sanitizeStringArray(source[key])
        : source[key];
    assignIfDefined(metadata as UnknownRecord, key, rawValue);
  });

  return metadata as LooksMetadataRequest;
};

const buildMovesMetadataRequest = (value: unknown): MovesMetadataRequest => {
  const source = asRecord(value);
  const metadata: Partial<MovesMetadataRequest> = {};

  MOVES_METADATA_KEYS.forEach(key => {
    const rawValue =
      key === 'mood' ||
      key === 'bodyParts' ||
      key === 'targetAudience' ||
      key === 'performanceContext' ||
      key === 'signatureMoves' ||
      key === 'techniquesRequired' ||
      key === 'propsNeeded'
        ? sanitizeStringArray(source[key])
        : source[key];
    assignIfDefined(metadata as UnknownRecord, key, rawValue);
  });

  return metadata as MovesMetadataRequest;
};

const buildWorldsMetadataRequest = (value: unknown): WorldsMetadataRequest => {
  const source = asRecord(value);
  const metadata: Partial<WorldsMetadataRequest> = {};

  WORLDS_METADATA_KEYS.forEach(key => {
    const rawValue =
      key === 'mood' ||
      key === 'targetAudience' ||
      key === 'performanceContext' ||
      key === 'culturalContext' ||
      key === 'dominantColors'
        ? sanitizeStringArray(source[key])
        : source[key];
    assignIfDefined(metadata as UnknownRecord, key, rawValue);
  });

  return metadata as WorldsMetadataRequest;
};

export const buildLayerAssetUpdateRequest = ({
  layer,
  description,
  tags,
  songMetadata,
  starMetadata,
  looksMetadata,
  movesMetadata,
  worldsMetadata,
}: BuildLayerAssetUpdateRequestParams): CanonicalLayerAssetUpdateRequest => {
  const basePayload = {
    description: description.trim(),
    tags: sanitizeTags(tags),
  };

  switch (layer) {
    case 'G':
      return {
        ...basePayload,
        metadata: buildSongMetadataRequest(songMetadata),
      };
    case 'S':
      return {
        ...basePayload,
        metadata: buildStarMetadataRequest(starMetadata),
      };
    case 'L':
      return {
        ...basePayload,
        metadata: buildLooksMetadataRequest(looksMetadata),
      };
    case 'M':
      return {
        ...basePayload,
        metadata: buildMovesMetadataRequest(movesMetadata),
      };
    case 'W':
      return {
        ...basePayload,
        metadata: buildWorldsMetadataRequest(worldsMetadata),
      };
    default:
      throw new Error(`Unsupported layer for canonical metadata update: ${layer}`);
  }
};
